import { useCallback, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import carImage from "../../../assets/images/Image 3.png";
import { useTranslation } from "../../../hooks/useTranslation";
import { getCompareVehicles, updateCompare } from "../../../pages/VehiclePage/utils";
import { VehicleNames } from "../../../utils/GeneralTypes";
import { FormatNumber, FormatPrice } from "../../../utils/generalUtils";
import { Chevron, Minus, Plus } from "../../Icons";
import style from "./index.module.css";

const SingleNewVehicleClass = ({
  vehicleNames,
  vehicleID,
  baseLink,
  classesNo,
  name,
  year,
  price,
  classes,
  image,
  hasNoClasses,
  setIsComparingModalActive,
  isShowroom = false,
}: ComponentPropsType) => {
  const { locale } = useTranslation();
  const [isClassesExpanded, setIsClassesExpanded] = useState<boolean>(false);
  const [vehicleClasses, setVehicleClasses] = useState(
    classes.map((vehicle) => ({
      ...vehicle,
      isCompared: false,
    })),
  );

  const history = useHistory();

  useEffect(() => {
    const currentCompare = getCompareVehicles();
    setVehicleClasses((current) =>
      current.map((cur) => ({
        ...cur,
        isCompared: currentCompare?.vehicles?.new?.includes(cur.id.toString()),
      })),
    );
  }, [vehicleNames]);

  const onClickVehicleHandler = useCallback(() => {
    if (hasNoClasses) history.push(`${baseLink}/${vehicleID}`);
    else setIsClassesExpanded((current) => !current);
  }, [hasNoClasses, history, baseLink, vehicleID]);

  const onToggleCompareHandler = useCallback(
    (id: number) => {
      const compareIsFull = updateCompare(vehicleNames.single, "new", id.toString() as string);
      if (compareIsFull && setIsComparingModalActive) {
        setIsComparingModalActive(true);
      } else {
        setVehicleClasses((current) =>
          current.map((cur) => ({
            ...cur,
            isCompared: cur.id === id ? !cur.isCompared : cur.isCompared,
          })),
        );
      }
    },
    [vehicleNames, setIsComparingModalActive],
  );

  return (
    <div className={style.car}>
      {!isClassesExpanded && !hasNoClasses && (
        <div className={style.car__classes_num}>
          {<FormatNumber value={classesNo} />} {locale(classesNo === 1 ? "class" : "classes")}
        </div>
      )}
      <div className={style.car__content} onClick={onClickVehicleHandler}>
        <div className={style.car__image}>
          <img src={image || carImage} alt="" />
        </div>
        <div className={style.car__details}>
          <div className={style.car__models}>
            <p>
              {locale("model")}: <b>{name}</b>
            </p>
            <p>
              {locale("year")}:{" "}
              <b>
                <FormatNumber value={year} />
              </b>
            </p>
          </div>
          {!isClassesExpanded && (
            <div className={style.car__price}>
              {+price === 0 ? (
                <p>{locale("currentlyUnavailableNewVehicle")}</p>
              ) : (
                <>
                  <span>{locale("starts_from")}</span>
                  <p>
                    <FormatPrice value={price} />
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {isClassesExpanded && !hasNoClasses && (
        <div className={style.car__classes}>
          {vehicleClasses.map((vehicle, index) => (
            <div key={index} className={style.car__single_class}>
              <button onClick={() => onToggleCompareHandler(vehicle.id)}>
                {vehicle.isCompared ? (
                  <Minus height="20" width="20" color="#dc0000" />
                ) : (
                  <Plus height="20" width="20" color="#dc0000" />
                )}
              </button>
              <Link to={`${baseLink}/${vehicle.id}`} className={style.car__single_class_content}>
                <p className={style.car__single_class_name}>
                  {vehicle.name}
                  {vehicle.selling_flag && !isShowroom && (
                    <div className={style.car__selling_flag}>{vehicle.selling_flag}</div>
                  )}
                </p>
                <div className={style.car__single_class_price}>
                  {+price === 0 ? (
                    <p>{locale("currentlyUnavailableNewVehicle")}</p>
                  ) : (
                    <p>
                      <FormatPrice value={vehicle.price} />
                    </p>
                  )}
                  <Chevron height="12" width="12" color="#2f2f2f" />
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SingleNewVehicleClass;

type ComponentPropsType = {
  vehicleNames: VehicleNames;
  baseLink: string;
  classesNo: number;
  name: string;
  image: string;
  year: number;
  price: string;
  classes: {
    name: string;
    price: string;
    id: number;
    selling_flag?: string;
  }[];
  hasNoClasses?: boolean;
  vehicleID?: number;
  setIsComparingModalActive?: CallableFunction;
  isShowroom?: boolean;
};
